/**
 * AppRegisterWizardPage — "App registrieren" (Image Builder 2.1 Phase C, #1712).
 *
 * 1 Quelle (Datei/Script; Intune is a locked teaser) -> 2 Rezept (saved as
 * `draft` on "Speichern & weiter", the API validates with the lab agent's
 * rules) -> 3 Prüfen (cost center, POST verify -> lab-test.yml) -> 4 Ergebnis.
 *
 * Step 3 polls every 10 s: the recipe (GET /apps/{appId}) until it carries
 * the labJobId of OUR dispatch (verification.dispatchId - an older run's job
 * must never be followed), then the job state and its events. Polling stops
 * when the job is cleaned up and the recipe has left `testing`; lab.ps1 writes
 * verified/failed back (#1710).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Step, StepLabel, Stepper, TextField, Typography } from '@mui/material';
import { appsApi, LabEvent, LabJob, recipeErrors, VerifyResult } from '../services/appsService';
import { costCentersApi } from '../services/costCentersService';
import { extractErrorMessage } from '../services/api';
import { AppStatus, CostCenterDefinition } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuth } from '../contexts/AuthContext';
import RecipeForm from '../components/apps/RecipeForm';
import LabCheckPanel from '../components/apps/LabCheckPanel';
import { emptyDraft, RecipeDraft, toRecipeInput, validateDraft } from '../utils/appRecipeDraft';
import { labResultMessage } from '../utils/labCheckSteps';
import { catalogStatus } from './AppCatalogPage';

const STEP_LABELS = ['Quelle', 'Rezept', 'Prüfen', 'Ergebnis'];
export const POLL_MS = 10_000;
/** After this many polls without our labJobId, say so (lab.ps1 writes it best effort). */
const LAB_JOB_WAIT_POLLS = 6;
/** Without a labJobId, stop after the API's stale window (VERIFY_STALE_MIN = 180 min): a new verify is allowed then. */
const LAB_JOB_GIVE_UP_MS = 180 * 60_000;

export default function AppRegisterWizardPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [draft, setDraft] = useState<RecipeDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const [costCenters, setCostCenters] = useState<CostCenterDefinition[]>([]);
  const [costCenter, setCostCenter] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [dispatch, setDispatch] = useState<VerifyResult | null>(null);

  const [labJobId, setLabJobId] = useState<string | null>(null);
  const [job, setJob] = useState<LabJob | null>(null);
  const [events, setEvents] = useState<LabEvent[]>([]);
  const [recipeStatus, setRecipeStatus] = useState<AppStatus | null>(null);
  const [polls, setPolls] = useState(0);
  const [pollError, setPollError] = useState<string | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const finishedRef = useRef(false);
  const inFlightRef = useRef(false);

  const { data: catalog } = useApiQuery(() => appsApi.list(), []);
  const errors = validateDraft(draft);
  const recipeId = `${draft.appId}@${draft.version}`;

  useEffect(() => {
    let cancelled = false;
    costCentersApi
      .list({ active: true })
      .then((data) => !cancelled && setCostCenters(data))
      .catch(() => !cancelled && setCostCenters([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // Step 3 poll loop. Keyed on the dispatch: a new verify restarts it.
  const { appId, version } = draft;
  useEffect(() => {
    if (!dispatch) return undefined;
    let cancelled = false;
    finishedRef.current = false;
    const startedAt = Date.now();
    const readRecipe = async () => (await appsApi.versions(appId)).find((v) => v.version === version);
    // The job, not the recipe, ends the poll: a cancelled run (lab stop), a vetoed
    // or failed write-back all reach `cleaned` while the recipe stays `testing`.
    const tick = async () => {
      if (inFlightRef.current) return;               // a slow request (SWA cold start) must not overlap
      inFlightRef.current = true;
      try {
        let recipe = await readRecipe();
        if (cancelled || !recipe) return;
        const v = recipe.verification;
        const jid = v && v.dispatchId === dispatch.dispatchId ? v.labJobId ?? null : null;
        if (jid) {
          setLabJobId(jid);
          const [j, e] = await Promise.all([appsApi.job(jid), appsApi.events(jid)]);
          if (cancelled) return;
          // The write-back runs before `cleaned`: re-read once so a write-back
          // that landed between our two reads is not missed.
          if (j.state === 'cleaned' && recipe.status === 'testing') recipe = (await readRecipe()) ?? recipe;
          if (cancelled) return;
          setJob(j);
          setEvents(e.events);
          finishedRef.current = j.state === 'cleaned';
        } else if (Date.now() - startedAt > LAB_JOB_GIVE_UP_MS) {
          finishedRef.current = true;
          setGaveUp(true);
        }
        setRecipeStatus(recipe.status);
        setPolls((n) => n + 1);
        setPollError(null);
      } catch (err) {
        if (!cancelled) setPollError(extractErrorMessage(err));
      } finally {
        inFlightRef.current = false;
      }
    };
    tick();
    const handle = setInterval(() => {
      if (!finishedRef.current) tick();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [dispatch, appId, version]);

  const finished = gaveUp || job?.state === 'cleaned';
  useEffect(() => {
    if (finished) setActiveStep(3);
  }, [finished]);

  if (!hasRole('operator')) {
    return <Alert severity="info">Nur Operatoren können Apps registrieren. Der Katalog ist unter „App-Katalog“ einsehbar.</Alert>;
  }

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    setServerErrors([]);
    try {
      await appsApi.upsert(toRecipeInput(draft), 'create');
      setActiveStep(2);
    } catch (err) {
      const rules = recipeErrors(err);
      setServerErrors(rules);
      setSaveError(rules.length ? null : extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const startCheck = async () => {
    setStarting(true);
    setStartError(null);
    try {
      setDispatch(await appsApi.verify(draft.appId, draft.version, costCenter));
    } catch (err) {
      setStartError(extractErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const downloadBundle = async (bundleAppId: string) => {
    if (!labJobId) return;
    try {
      window.open(await appsApi.bundleUrl(labJobId, bundleAppId), '_blank', 'noopener');
    } catch (err) {
      setPollError(extractErrorMessage(err));
    }
  };

  const hasDraftErrors = Object.keys(errors).length > 0;
  const result = labResultMessage({
    rootId: recipeId,
    outcome: job?.outcome,
    writeBack: job?.writeBack,
    recipeStatus,
    events,
    failClass: job?.failClass,
    gaveUp,
  });

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          App registrieren
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/apps')}>
          {dispatch ? 'Zum Katalog' : 'Abbrechen'}
        </Button>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Quelle
              </Typography>
              <Box display="flex" gap={2} alignItems="flex-start">
                <Button variant="contained" aria-pressed="true">
                  Datei / Script
                </Button>
                <Box>
                  <Button variant="outlined" disabled sx={{ opacity: 0.6 }}>
                    Microsoft Intune
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Kommt mit Intune-Import
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <RecipeForm draft={draft} errors={errors} catalog={catalog ?? []} serverErrors={serverErrors} onChange={setDraft} />
          )}
          {activeStep === 1 && saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Prüfen: {recipeId}
              </Typography>
              {!dispatch && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Die Prüfung installiert das Rezept samt Voraussetzungen auf einer frischen Test-VM aus der aktuellen
                    Basis und löscht die VM danach. Dauer je nach App ca. 5–60 min.
                  </Alert>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Kostenstelle"
                    value={costCenter}
                    onChange={(e) => setCostCenter(e.target.value)}
                    helperText={costCenters.length === 0 ? 'Keine aktive Kostenstelle verfügbar' : 'Die Test-VM wird dieser Kostenstelle zugeordnet'}
                  >
                    {costCenters.map((cc) => (
                      <MenuItem key={cc.code} value={cc.code}>
                        {cc.displayName} ({cc.code})
                      </MenuItem>
                    ))}
                  </TextField>
                  {startError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {startError}
                    </Alert>
                  )}
                </>
              )}
              {dispatch && (
                <Box>
                  {dispatch.jobUrl && (
                    <Button variant="text" href={dispatch.jobUrl} target="_blank" rel="noopener noreferrer" sx={{ mb: 1 }}>
                      GitHub-Run
                    </Button>
                  )}
                  {!labJobId && (
                    <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>
                      {polls >= LAB_JOB_WAIT_POLLS
                        ? 'Der Lab-Job meldet sich noch nicht. Der Status des Rezepts wird weiter abgefragt.'
                        : 'Warte auf den Start des Lab-Jobs …'}
                    </Alert>
                  )}
                  {pollError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {pollError}
                    </Alert>
                  )}
                  {labJobId && (
                    <LabCheckPanel
                      rootId={recipeId}
                      rootAppId={draft.appId}
                      prereqIds={dispatch.recipes.filter((r) => r !== recipeId)}
                      job={job}
                      events={events}
                      onDownloadBundle={downloadBundle}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Ergebnis: {recipeId}
              </Typography>
              {recipeStatus && <Chip label={catalogStatus({ status: recipeStatus, required: false }).label} color={catalogStatus({ status: recipeStatus, required: false }).color} sx={{ mb: 2 }} />}
              <Alert severity={result.severity} sx={{ mb: 2 }}>
                {result.text}
              </Alert>
              <LabCheckPanel
                rootId={recipeId}
                rootAppId={draft.appId}
                prereqIds={(dispatch?.recipes ?? []).filter((r) => r !== recipeId)}
                job={job}
                events={events}
                onDownloadBundle={downloadBundle}
              />
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button onClick={() => setActiveStep((s) => s - 1)} sx={{ visibility: activeStep === 1 ? 'visible' : 'hidden' }} disabled={activeStep !== 1}>
              Zurück
            </Button>
            {activeStep === 0 && (
              <Button variant="contained" onClick={() => setActiveStep(1)}>
                Weiter
              </Button>
            )}
            {activeStep === 1 && (
              <Button variant="contained" onClick={save} disabled={saving || hasDraftErrors}>
                Speichern & weiter
              </Button>
            )}
            {activeStep === 2 && !dispatch && (
              <Button variant="contained" onClick={startCheck} disabled={starting || costCenter === ''}>
                Prüfung starten
              </Button>
            )}
            {activeStep === 3 && (
              <Button variant="contained" onClick={() => navigate('/apps')}>
                Zum Katalog
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
